import { ArrowLeft, Camera } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/select";

import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

const EditProfile = () => {
  const navigate = useNavigate();
  const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profileImage") || ""
  );

  // Get preferred units from preferences
  const preferredHeightUnit =
    localStorage.getItem("preferredHeightUnit") || "ft-inch";
  const preferredWeightUnit =
    localStorage.getItem("preferredWeightUnit") || "kg";

  const [formData, setFormData] = useState({
    name: userProfile.name || "Rajesh Shah",
    email: userProfile.email || "rajesh@gmail.com",
    gender: userProfile.gender || "Male",
    dateOfBirth: userProfile.dateOfBirth || "1998-01-15",
    heightFt: userProfile.heightFt || "",
    heightIn: userProfile.heightIn || "",
    heightCm: userProfile.heightCm || "170",
    weightLbs: userProfile.weightLbs || "",
    weightKg: userProfile.weightKg || "68",
    bloodType: userProfile.bloodType || "O+",
    race: userProfile.race || "Asian",
  });

  const genderOptions = ["Male", "Female", "Other"];
  const raceOptions = [
    "White",
    "Black or African American",
    "Asian",
    "American Indian or Alaska Native",
    "Native Hawaiian or Other Pacific Islander",
    "Middle Eastern or North African",
  ];
  const bloodTypeOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

  const handleSave = () => {
    localStorage.setItem("userProfile", JSON.stringify(formData));
    toast.success("Profile updated successfully!");
    navigate("/profile");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/profile")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Edit Profile</h1>
          <div className="w-10" />
        </div>

        {/* Profile Picture */}
        <Card className="rounded-2xl border-0 p-6 shadow-sm">
          <div className="flex flex-col items-center">
            <Avatar className="mb-4 h-24 w-24 border-4 border-primary/10">
              {profileImage ? (
                <AvatarImage src={profileImage} alt="Profile" />
              ) : (
                <AvatarFallback className="to-chart-1 bg-gradient-to-br from-primary text-2xl font-bold text-white">
                  {formData.name
                    .split(" ")
                    .map(n => n[0])
                    .join("")}
                </AvatarFallback>
              )}
            </Avatar>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg"
              onClick={() => navigate("/profile/photo/upload")}
            >
              <Camera className="mr-2 h-4 w-4" />
              Change Photo
            </Button>
          </div>
        </Card>

        {/* Personal Information */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">
            Personal Information
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={e =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={value =>
                    setFormData({ ...formData, gender: value })
                  }
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {genderOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={e =>
                    setFormData({ ...formData, dateOfBirth: e.target.value })
                  }
                  className="rounded-xl"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {/* Height based on preference */}
            <div className="space-y-2">
              <Label>
                Height ({preferredHeightUnit === "ft-inch" ? "Ft-Inch" : "CM"})
              </Label>
              {preferredHeightUnit === "ft-inch" ? (
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number"
                    placeholder="Feet"
                    value={formData.heightFt}
                    onChange={e =>
                      setFormData({ ...formData, heightFt: e.target.value })
                    }
                    className="rounded-xl"
                  />
                  <Input
                    type="number"
                    placeholder="Inches"
                    value={formData.heightIn}
                    onChange={e =>
                      setFormData({ ...formData, heightIn: e.target.value })
                    }
                    className="rounded-xl"
                  />
                </div>
              ) : (
                <Input
                  type="number"
                  placeholder="CM"
                  value={formData.heightCm}
                  onChange={e =>
                    setFormData({ ...formData, heightCm: e.target.value })
                  }
                  className="rounded-xl"
                />
              )}
            </div>

            {/* Weight based on preference */}
            <div className="space-y-2">
              <Label>
                Weight ({preferredWeightUnit === "kg" ? "Kg" : "Lbs"})
              </Label>
              <Input
                type="number"
                placeholder={preferredWeightUnit === "kg" ? "Kg" : "Lbs"}
                value={
                  preferredWeightUnit === "kg"
                    ? formData.weightKg
                    : formData.weightLbs
                }
                onChange={e =>
                  setFormData({
                    ...formData,
                    [preferredWeightUnit === "kg" ? "weightKg" : "weightLbs"]:
                      e.target.value,
                  })
                }
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="bloodType">Blood Type</Label>
                <Select
                  value={formData.bloodType}
                  onValueChange={value =>
                    setFormData({ ...formData, bloodType: value })
                  }
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select blood type" />
                  </SelectTrigger>
                  <SelectContent>
                    {bloodTypeOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="race">Race/Ethnicity</Label>
                <Select
                  value={formData.race}
                  onValueChange={value =>
                    setFormData({ ...formData, race: value })
                  }
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Select race" />
                  </SelectTrigger>
                  <SelectContent>
                    {raceOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* Save Button */}
        <Button onClick={handleSave} className="h-12 w-full rounded-xl">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default EditProfile;
